export function formatDateDDMMYYYY(value: Date | string | number): string {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear());

  return `${day}-${month}-${year}`;
}

/**
 * ISO dates (`1985-05-12`, `1985-05-12T00:00:00`, `...Z`, `...+01:00`) as written
 * by the API. The date parts are read as written, not through `new Date(iso)`,
 * which would read a date-only string as UTC and shift it a day west of GMT.
 */
const ISO_DATE_IN_TEXT = /\b(\d{4})-(\d{2})-(\d{2})(?:T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?)?\b/g;

/**
 * UK dates the API writes out already formatted (`12/02/1990`) rather than as ISO
 * — the policy-template token values do this. Only the separator changes, so the
 * day and month are never reinterpreted.
 */
const UK_SLASH_DATE_IN_TEXT = /\b(\d{2})\/(\d{2})\/(\d{4})\b/g;

/** Rewrites every date inside free text in the `formatDateDDMMYYYY` format. */
export function formatDatesInText(text: string): string {
  return String(text ?? '')
    .replace(ISO_DATE_IN_TEXT, (match, year: string, month: string, day: string) => {
      const date = new Date(Number(year), Number(month) - 1, Number(day));
      return date.getMonth() === Number(month) - 1 ? formatDateDDMMYYYY(date) : match;
    })
    .replace(UK_SLASH_DATE_IN_TEXT, (match, day: string, month: string, year: string) =>
      Number(month) >= 1 && Number(month) <= 12 && Number(day) >= 1 && Number(day) <= 31
        ? `${day}-${month}-${year}`
        : match,
    );
}

export function formatDateLong(value: Date | string | number): string {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
}

export function formatDateTime(value: Date | string | number): string {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear());
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${day}/${month}/${year}, ${hours}:${minutes}`;
}
